import {BehaviorSubject, isObservable, Observable, Subject} from 'rxjs';
import {Data} from '@angular/router';

export type ReactiveActionName = 'add'
  | 'select'
  | 'save'
  | 'create'
  | 'delete'
  | 'deleteFile'
  | 'deleteGroup'
  | 'selectGroup'
  | 'groupEdit'
  | 'groupRole'
  | 'groupAdd'
  | 'groupParamAdd'
  | 'groupRemove'
  | 'update'
  | 'unlink'
  | 'saveas'
  | 'getCsv'
  | 'upload'
  | 'reload'
  | 'custom'
  | 'failed'
  | 'link'
  | 'copy';

export interface ReactiveAction {
  name: ReactiveActionName;
  data?: any;
  params?: any;
}

export type ReactiveEventStatus = 'start' | 'done' | 'single' | 'error';

export interface ReactiveEvent {
  action: ReactiveAction;
  status: ReactiveEventStatus;
  message?: string;
  result?: any;

}


export const matchActionStatus = (status: ReactiveEventStatus, actions: ReactiveActionName[]) => {
  return (event: ReactiveEvent) => event.status === status && actions.includes(event.action.name);
};

export const matchStatus = (status: ReactiveEventStatus) => {
  return (event: ReactiveEvent) => event.status === status;
};

export const matchActions = (actions: ReactiveActionName[]) => {
  return (event: ReactiveEvent) => actions.includes(event.action.name);
};


export abstract class ReactiveService {
  private _filtered = new BehaviorSubject<any>(null);
  get filtered() {
    return this._filtered;
  }
  private _data = new BehaviorSubject<any>(null);
  get data() {
    return this._data.asObservable();
  }

  private _snapshot: Data = {};
  get snapshot() {
    return this._snapshot;
  }

  _snapshots: { [name: string]: { data: any, time: number } } = {};
  get snapshots() {
    return this._snapshots;
  }

  private _events = new Subject<ReactiveEvent>();
  get events() {
    return this._events;
  }

  filterData<T>(cb: (value: T) => boolean): void {
    if (this._data.value === null) {
      this._filtered.next(null);
    }
    this._filtered.next(this._data.value.filter(cb));
  }

  updateData(data: Observable<any> | any) {
    if (isObservable(data)) {
      this.events.next({action: {name: 'update'}, status: 'start'});
      data.subscribe(
        result => this.updateData(result),
        error => this.events.next({action: {name: 'update'}, status: 'error'})
      );
      return;
    }
    this.snapshot.data = data;
    this._data.next(data);
    this._filtered.next(data);
    this.events.next({action: {name: 'update'}, status: 'done'});
  }


  request(name, request) {
    this.events.next({action: {name: 'update'}, status: 'start'});
    this.setSnapshot(name, true);
    request.subscribe(
      data => {
        this.setSnapshot(name, data);
        this._data.next(data);
        this.events.next({action: {name: 'update'}, status: 'done'});
      },
      error => {
        this.deleteSnapshot(name);
        this.events.next({action: {name: 'update'}, status: 'error'});
      }
    );
  }

  setSnapshot(name, data) {
    this._snapshots[name] = {
      data,
      time: Date.now()
    };
  }

  deleteSnapshot(name) {
    this._snapshots[name] = null;
  }

  getSnapshot(name, timeout) {
    if (!this.snapshots[name] || (this.snapshots[name].time + timeout < Date.now())) {
      return null;
    }
    return this.snapshots[name];
  }

  sendEvent<T = any>(action: ReactiveAction, producer: Observable<T>) {
    this.events.next({action, status: 'start'});
    if (isObservable(producer)) {
      producer.subscribe(
        data => this.events.next({action, status: 'done', result: data}),
        error => this.events.next({action, status: 'error', result: error})
        );
    }
  }

  sendAction(action: ReactiveAction) {
    this.events.next({action, status: 'single'});
  }
}
